<template>
  <Transition name="slide-fade">
    <div v-if="isVisible" class="fixed bottom-6 left-0 right-0 z-[9999] flex justify-center px-4">
      <div
        class="cookie-consent-card max-w-xl w-full md:w-[560px] p-5 rounded-2xl border flex flex-col gap-4 text-left transition-all duration-300 ease-in-out">
        <!-- Mode: Main Banner -->
        <div v-if="mode === 'banner'" class="flex flex-col gap-4">
          <div class="flex items-start gap-4">
            <!-- Animated Cookie Icon Container -->
            <div class="cookie-icon-wrapper flex items-center justify-center p-3 rounded-xl shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 animate-pulse" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
                <circle cx="8.5" cy="8.5" r="1" fill="currentColor" />
                <circle cx="16" cy="15.5" r="1" fill="currentColor" />
                <circle cx="12" cy="12" r="1" fill="currentColor" />
                <circle cx="11" cy="17" r="1" fill="currentColor" />
                <circle cx="7" cy="14" r="1" fill="currentColor" />
              </svg>
            </div>
            <div class="flex flex-col gap-1">
              <h3 class="cookie-title font-[600] leading-snug">
                {{ $t('landing.cookieConsent.title') }}
              </h3>
              <p class="cookie-description leading-relaxed">
                {{ $t('landing.cookieConsent.description') }}
              </p>
            </div>
          </div>

          <div
            class="cookie-divider flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 pt-3 border-t w-full">
            <NuxtLink to="/privacyPolicy" class="text-primary font-medium underline text-sm">
              {{ $t('landing.privacy.title') }}
            </NuxtLink>
            <div class="flex flex-col md:flex-row items-stretch md:items-center gap-2 w-full md:w-auto md:ml-auto">
              <!-- Customize Button -->
              <button @click="mode = 'customize'"
                class="cookie-btn-secondary font-semibold transition-colors duration-200 rounded-lg w-full md:w-auto text-center">
                {{ $t('landing.cookieConsent.customize') }}
              </button>

              <!-- Decline Button -->
              <button @click="declineCookies"
                class="cookie-btn-secondary font-semibold transition-colors duration-200 rounded-lg w-full md:w-auto text-center">
                {{ $t('landing.cookieConsent.decline') }}
              </button>

              <!-- Accept All Button -->
              <button @click="acceptAllCookies"
                class="cookie-btn-primary font-semibold active:scale-95 transition-all duration-200 rounded-lg w-full md:w-auto text-center">
                {{ $t('landing.cookieConsent.accept') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Mode: Custom Settings -->
        <div v-else-if="mode === 'customize'" class="flex flex-col gap-4">
          <div class="cookie-divider flex items-center justify-between pb-2 border-b">
            <h3 class="cookie-title font-[600]">
              {{ $t('landing.cookieConsent.customize') }}
            </h3>
            <button @click="mode = 'banner'"
              class="cookie-btn-back font-semibold flex items-center gap-1 transition-colors duration-200">
              <!-- Back Arrow -->
              <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24"
                stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              {{ $t('landing.cookieConsent.back') }}
            </button>
          </div>

          <!-- Cookie Preferences List -->
          <div class="flex flex-col gap-4 py-1 max-h-[220px] overflow-y-auto scrollbar-thin">
            <!-- Necessary Cookies -->
            <div class="flex items-start justify-between gap-4">
              <div class="flex flex-col gap-0.5">
                <h4 class="cookie-item-title font-[600]">
                  {{ $t('landing.cookieConsent.necessaryTitle') }}
                </h4>
                <p class="cookie-item-desc leading-normal">
                  {{ $t('landing.cookieConsent.necessaryDesc') }}
                </p>
              </div>
              <!-- Disabled Active Custom Switch -->
              <div class="cookie-switch-bg-disabled w-9 h-5 rounded-full relative cursor-not-allowed shrink-0">
                <div class="w-4 h-4 bg-white rounded-full absolute top-[2px] right-[2px]"></div>
              </div>
            </div>

            <!-- Analytics Cookies -->
            <div class="flex items-start justify-between gap-4">
              <div class="flex flex-col gap-0.5">
                <h4 class="cookie-item-title font-[600]">
                  {{ $t('landing.cookieConsent.analyticsTitle') }}
                </h4>
                <p class="cookie-item-desc leading-normal">
                  {{ $t('landing.cookieConsent.analyticsDesc') }}
                </p>
              </div>
              <!-- Toggle Switch -->
              <button @click="prefs.analytics = !prefs.analytics"
                class="cookie-switch-bg w-9 h-5 rounded-full relative transition-colors duration-200 shrink-0 outline-none"
                :class="{ 'active': prefs.analytics }">
                <div
                  class="w-4 h-4 bg-white rounded-full absolute top-[2px] left-[2px] transition-transform duration-200 shadow"
                  :class="{ 'translate-x-[16px]': prefs.analytics }"></div>
              </button>
            </div>

            <!-- Marketing Cookies -->
            <div class="flex items-start justify-between gap-4">
              <div class="flex flex-col gap-0.5">
                <h4 class="cookie-item-title font-[600]">
                  {{ $t('landing.cookieConsent.marketingTitle') }}
                </h4>
                <p class="cookie-item-desc leading-normal">
                  {{ $t('landing.cookieConsent.marketingDesc') }}
                </p>
              </div>
              <!-- Toggle Switch -->
              <button @click="prefs.marketing = !prefs.marketing"
                class="cookie-switch-bg w-9 h-5 rounded-full relative transition-colors duration-200 shrink-0 outline-none"
                :class="{ 'active': prefs.marketing }">
                <div
                  class="w-4 h-4 bg-white rounded-full absolute top-[2px] left-[2px] transition-transform duration-200 shadow"
                  :class="{ 'translate-x-[16px]': prefs.marketing }"></div>
              </button>
            </div>
          </div>

          <div
            class="cookie-divider flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 pt-3 border-t w-full">
            <div class="flex flex-col md:flex-row items-stretch md:items-center gap-2 w-full md:w-auto md:ml-auto">
              <!-- Save Button -->
              <button @click="savePreferences"
                class="cookie-btn-save font-semibold active:scale-95 transition-all duration-200 rounded-lg w-full md:w-auto text-center">
                {{ $t('landing.cookieConsent.save') }}
              </button>
              <!-- Accept All Button -->
              <button @click="acceptAllCookies"
                class="cookie-btn-primary font-semibold active:scale-95 transition-all duration-200 rounded-lg w-full md:w-auto text-center">
                {{ $t('landing.cookieConsent.accept') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'

const isVisible = ref(false)
const mode = ref('banner') // 'banner' | 'customize'

const prefs = reactive({
  necessary: true,
  analytics: false,
  marketing: false
})

onMounted(() => {
  // Check if user has already made a choice
  const consent = localStorage.getItem('cookie-consent')
  if (!consent) {
    // Show banner after an elegant short delay
    setTimeout(() => {
      isVisible.value = true
    }, 1200)
  } else {
    // If they have choices saved, pre-populate prefs state
    try {
      const parsed = JSON.parse(consent)
      if (typeof parsed === 'object') {
        Object.assign(prefs, parsed)
      }
    } catch {
      if (consent === 'accepted') {
        prefs.analytics = true
        prefs.marketing = true
      }
    }
  }
})

const acceptAllCookies = () => {
  prefs.analytics = true
  prefs.marketing = true
  localStorage.setItem('cookie-consent', JSON.stringify(prefs))
  isVisible.value = false
}

const declineCookies = () => {
  prefs.analytics = false
  prefs.marketing = false
  localStorage.setItem('cookie-consent', JSON.stringify(prefs))
  isVisible.value = false
}

const savePreferences = () => {
  localStorage.setItem('cookie-consent', JSON.stringify(prefs))
  isVisible.value = false
}
</script>

<style scoped>
/* --- Typography and paddings (Light mode default) --- */
.cookie-title {
  color: #111827;
  font-size: 14px;
}

.cookie-description {
  color: #4b5563;
  font-size: 12px;
}

.cookie-btn-secondary,
.cookie-btn-primary,
.cookie-btn-save,
.cookie-btn-back {
  font-size: 12px;
}

.cookie-btn-secondary,
.cookie-btn-save {
  padding: 8px 16px;
}

.cookie-btn-primary {
  padding: 8px 20px;
}

.cookie-item-title {
  color: #1f2937;
  font-size: 13px;
}

.cookie-item-desc {
  color: #6b7280;
  font-size: 11px;
}

/* Responsive styles using CSS media queries (Desktop >= 768px) */
@media (min-width: 768px) {
  .cookie-title {
    font-size: 16px;
  }

  .cookie-description {
    font-size: 14px;
  }

  .cookie-btn-secondary,
  .cookie-btn-primary,
  .cookie-btn-save,
  .cookie-btn-back {
    font-size: 14px;
  }

  .cookie-btn-secondary,
  .cookie-btn-save {
    padding: 8px 16px;
  }

  .cookie-btn-primary {
    padding: 8px 18px;
  }

  .cookie-item-title {
    font-size: 14px;
  }

  .cookie-item-desc {
    font-size: 13px;
  }
}

/* --- Colors & Theme Styles: Light mode (default) --- */
.cookie-consent-card {
  background-color: rgba(255, 255, 255);
  border-color: rgba(226, 232, 240, 0.8);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.08);
}

.cookie-icon-wrapper {
  background-color: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

.cookie-divider {
  border-color: rgba(229, 231, 235, 0.8);
}

.cookie-btn-secondary {
  color: #4b5563;
  background-color: transparent;
}

.cookie-btn-secondary:hover {
  color: #111827;
  background-color: #f3f4f6;
}

.cookie-btn-primary {
  color: #ffffff;
  background-color: #16a34a;
}

.cookie-btn-primary:hover {
  background-color: #15803d;
}

.cookie-btn-back {
  color: #6b7280;
}

.cookie-btn-back:hover {
  color: #111827;
}

.cookie-switch-bg-disabled {
  background-color: #16a34a;
  opacity: 0.6;
}

.cookie-switch-bg {
  background-color: #e5e7eb;
}

.cookie-switch-bg.active {
  background-color: #16a34a;
}

.cookie-btn-save {
  color: #374151;
  background-color: #f3f4f6;
}

.cookie-btn-save:hover {
  background-color: #e5e7eb;
}

/* --- Dark mode --- */
.dark .cookie-consent-card {
  background-color: rgba(42, 51, 61, 1);
  border-color: rgba(74, 85, 104, 0.4);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
}

.dark .cookie-icon-wrapper {
  background-color: rgba(74, 222, 128, 0.1);
  color: #4ade80;
}

.dark .cookie-title {
  color: #ffffff;
}

.dark .cookie-description {
  color: #9cb3c9;
}

.dark .cookie-divider {
  border-color: rgba(74, 85, 104, 0.4);
}

.dark .cookie-btn-secondary {
  color: #8d96a1;
}

.dark .cookie-btn-secondary:hover {
  color: #ffffff;
  background-color: #2a333d;
}

.dark .cookie-btn-primary {
  color: #ffffff;
  background-color: #16a34a;
}

.dark .cookie-btn-primary:hover {
  background-color: #22c55e;
}

.dark .cookie-btn-back {
  color: #8d96a1;
}

.dark .cookie-btn-back:hover {
  color: #ffffff;
}

.dark .cookie-item-title {
  color: #ffffff;
}

.dark .cookie-item-desc {
  color: #8d96a1;
}

.dark .cookie-switch-bg-disabled {
  background-color: #22c55e;
  opacity: 0.6;
}

.dark .cookie-switch-bg {
  background-color: #2a333d;
}

.dark .cookie-switch-bg.active {
  background-color: #22c55e;
}

.dark .cookie-btn-save {
  color: #ffffff;
  background-color: #2a333d;
}

.dark .cookie-btn-save:hover {
  background-color: #4a5568;
}

/* --- Transitions & Animations --- */
.slide-fade-enter-active {
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.7, 0, 0.84, 0);
}

.slide-fade-enter-from {
  transform: translateY(30px) scale(0.95);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(30px) scale(0.95);
  opacity: 0;
}

/* Custom scrollbar for custom settings view */
.scrollbar-thin::-webkit-scrollbar {
  width: 4px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.25);
  border-radius: 9999px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.45);
}
</style>
